import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  useBuyAndReplaceUserRolesMutation,
  useDeleteUserShareSettingsMutation,
  useGetUserByIdQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
  useUpdateUserRolesMutation,
} from '../../store/user/user'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { getUserName } from '../utils'
import { getRoleGroupForRoles, useInviteRoleGroups } from './InviteRoleGroups'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { useGetRolesQuery } from '../../store/role/role'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { Role, RoleType } from '../../models/Role'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { addToast } from '../../store/toast/toastSlice'
import { UserShareForm } from './UserShareForm'
import { DEFAULT_SESSION_SHARING_ENABLED, useGetAccountUserSettingsQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useInviteFlowRevampEnabled } from './useInviteFlowRevampEnabled'
import { useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { getRoleChangeSeatImpact, type RoleChangeSeatImpact, seatTypeLabels } from './roleChangeSeatImpact'
import { readRoleChangeBuyResult } from './roleChangeBuyResult'
import { ASSISTANT_SEATS_NOT_OFFERED, ASSISTANT_SEATS_UPGRADE } from './assistantSeats'
import { SeatInfoBox } from './SeatInfoBox'
import { SeatPurchaseSummary } from './SeatPurchaseSummary'
import { useAssistantSeatsOffered } from './useAssistantSeatsOffered'
import { useSeatPurchaseGate } from './seatPurchaseGate'
import { useSeatPurchasePreview } from './useSeatPurchasePreview'
import { useRefreshSeats } from './useRefreshSeats'
import { isApiErrors } from '../../models/ApiErrors'

export interface EditUserModalProps {
  userId: string
  onHide: () => void
  onUserEdited?: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  roleIds: string[]
}

interface RolePurchaseTarget {
  data: FormValues
  seatType: RoleType
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ userId, onHide, onUserEdited }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const { data: userData, isLoading, isFetching: userDataFetching } = useGetUserByIdQuery({ accountId, userId }, { refetchOnMountOrArgChange: true })
  const isDeactivated = userData?.data.status === 'deactivated'

  const { hasQuota } = useZocksQuota()
  const documentSharingAvailable = hasQuota('ai.documentSummaryAgent.value')

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [sharingOverride, setSharingOverride] = useState<boolean | null>(null)

  const [saveUserShareSettings] = useSaveUserShareSettingsMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()
  const [saveUserByIdForSharing] = useSaveUserByIdMutation()

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery({ accountId })

  const defaultSharingEnabled = useMemo(() => {
    if (!userData || !accountUserSettings) {
      return DEFAULT_SESSION_SHARING_ENABLED
    }
    const userSetting = userData.data.settings
    if (userSetting) {
      return !!(userSetting.contactSharingEnabled && userSetting.sessionSharingEnabled)
    }
    const settingsOverride = accountUserSettings.data?.settingsOverride
    if (settingsOverride) {
      return !!(settingsOverride.sessionSharingEnabled && settingsOverride.contactSharingEnabled)
    }
    return DEFAULT_SESSION_SHARING_ENABLED
  }, [userData, accountUserSettings])

  const sharingEnabled = sharingOverride ?? defaultSharingEnabled

  const handleSharingEnabledChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSharingOverride(e.currentTarget.checked)
  }

  const handleShareSave = useCallback(async () => {
    if (!userData) {
      return
    }
    try {
      setIsSaving(true)
      await saveUserByIdForSharing({
        accountId,
        userId,
        userData: {
          settings: {
            sessionSharingEnabled: sharingEnabled,
            contactSharingEnabled: sharingEnabled,
            ...(documentSharingAvailable ? { documentSharingEnabled: sharingEnabled } : {}),
          },
        },
      }).unwrap()
      if (sharingEnabled) {
        await saveUserShareSettings({
          accountId,
          userId,
          payload: { shareType: 'meeting', sharedWithUserIds: selectedUserIds },
        }).unwrap()
        await saveUserShareSettings({
          accountId,
          userId,
          payload: { shareType: 'contact', sharedWithUserIds: selectedUserIds },
        }).unwrap()
        if (documentSharingAvailable) {
          await saveUserShareSettings({
            accountId,
            userId,
            payload: { shareType: 'document', sharedWithUserIds: selectedUserIds },
          }).unwrap()
        }
      } else {
        await deleteUserShareSettings({ accountId, userId, shareType: 'meeting' }).unwrap()
        await deleteUserShareSettings({ accountId, userId, shareType: 'contact' }).unwrap()
        if (documentSharingAvailable) {
          await deleteUserShareSettings({ accountId, userId, shareType: 'document' }).unwrap()
        }
      }
      dispatch(addToast({ variant: 'success', title: 'Sharing updated', messages: ['Sharing settings have been saved.'] }))
      setIsSaving(false)
      onUserEdited?.()
      onHide()
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Save failed', messages: ['Failed to save sharing settings. Please try again.'] }))
      setIsSaving(false)
    }
  }, [
    userData,
    onHide,
    onUserEdited,
    saveUserShareSettings,
    deleteUserShareSettings,
    saveUserByIdForSharing,
    accountId,
    dispatch,
    selectedUserIds,
    userId,
    sharingEnabled,
    documentSharingAvailable,
  ])

  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveUserByIdMutation()
  const [updateUserRoles, { isLoading: isRolesUpdating, isSuccess: updateRolesSuccess }] = useUpdateUserRolesMutation()

  const form = useForm<FormValues>({
    defaultValues: { firstName: '', lastName: '', email: '', phoneNumber: '', roleIds: [] },
  })
  const { handleSubmit } = form

  const { isEnabled: seatGateEnabled, isLoading: seatGateLoading } = useInviteFlowRevampEnabled()
  const canBuySeats = useSeatPurchaseGate()
  const assistantOffer = useAssistantSeatsOffered()
  const refreshSeats = useRefreshSeats()
  const { trackingClient } = useTracking()
  const [buyAndReplaceUserRoles, { isLoading: isBuying }] = useBuyAndReplaceUserRolesMutation()

  const seatGateActive = seatGateEnabled && !isDeactivated
  const {
    data: freeSeatsData,
    isSuccess: freeSeatsLoaded,
    isError: freeSeatsFailed,
  } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { skip: !seatGateActive, refetchOnMountOrArgChange: true })
  const freeSeatsPending = seatGateActive && ((!freeSeatsLoaded && !freeSeatsFailed) || assistantOffer.isLoading)
  const selectedRoleIds = useWatch({ control: form.control, name: 'roleIds' })
  const [roleChangeFailure, setRoleChangeFailure] = useState<{ roleIds: string[]; message: string } | null>(null)
  const roleChangeError = roleChangeFailure && sameRoleIds(roleChangeFailure.roleIds, selectedRoleIds ?? []) ? roleChangeFailure.message : null
  const [purchaseTarget, setPurchaseTarget] = useState<RolePurchaseTarget | null>(null)
  const [buyError, setBuyError] = useState<string | null>(null)

  const seatImpact: RoleChangeSeatImpact = seatGateEnabled
    ? getRoleChangeSeatImpact({
        currentRoleIds: userData?.data.roles.map((role) => role.id) ?? [],
        selectedRoleIds: selectedRoleIds ?? [],
        seats: freeSeatsData?.data.seats,
        assistantOffered: assistantOffer.offered,
      })
    : { kind: 'unchanged' }

  const purchase = useMemo(
    () => ({ general: purchaseTarget?.seatType === RoleType.SEAT ? 1 : 0, sidecar: purchaseTarget?.seatType === RoleType.SIDECAR ? 1 : 0 }),
    [purchaseTarget?.seatType],
  )
  const preview = useSeatPurchasePreview(purchase)

  const buildProfileUpdate = (data: FormValues) => ({
    accountId,
    userId,
    userData: {
      firstName: data.firstName,
      lastName: data.lastName,
      name: getUserName(data.firstName, data.lastName) || undefined,
      email: data.email,
    },
  })

  const saveProfile = async (data: FormValues) => {
    try {
      await saveUserById(buildProfileUpdate(data)).unwrap()
      return true
    } catch (error) {
      dispatch(addToast({ variant: 'error', title: 'Save failed', messages: [readApiMessage(error, 'Failed to save the user details. Please try again.')] }))
      return false
    }
  }

  const handleEditSave = async (data: FormValues) => {
    if (!seatGateEnabled) {
      saveUserById(buildProfileUpdate(data))
      updateUserRoles({ accountId, userId, roleIds: data.roleIds })
      return
    }

    if (seatImpact.kind === 'blocked' && canBuySeats) {
      setRoleChangeFailure(null)
      setBuyError(null)
      setPurchaseTarget({ data, seatType: seatImpact.seatType })
      return
    }

    setRoleChangeFailure(null)
    if (!(await saveProfile(data))) {
      return
    }

    try {
      await updateUserRoles({ accountId, userId, roleIds: data.roleIds }).unwrap()
    } catch (error) {
      setRoleChangeFailure({ roleIds: data.roleIds, message: readApiMessage(error, 'The role could not be changed.') })
    } finally {
      refreshSeats()
    }
  }

  const handleBuyConfirm = async () => {
    if (!purchaseTarget) {
      return
    }

    const { data, seatType } = purchaseTarget
    setBuyError(null)
    let verdict
    try {
      const response = await buyAndReplaceUserRoles({ accountId, userId, roleIds: data.roleIds, purchase }).unwrap()
      verdict = readRoleChangeBuyResult(response.data)
    } catch {
      verdict = readRoleChangeBuyResult(undefined)
    }

    refreshSeats()

    if (!verdict.applied) {
      setBuyError(verdict.message ?? null)
      return
    }

    if (verdict.bought) {
      trackingClient.track({ eventName: TrackEvent.SEAT_PURCHASED, eventDetails: { email: data.email, seatType } })
    }

    if ((await saveProfile(data)) && verdict.bought) {
      dispatch(addToast({ variant: 'success', title: 'Seat purchased. Role updated' }))
    }

    onUserEdited?.()
    onHide()
  }

  useEffect(() => {
    if (!isDeactivated && isSuccess && updateRolesSuccess) {
      onUserEdited?.()
      onHide()
    }
  }, [isSuccess, isDeactivated, updateRolesSuccess, onUserEdited, onHide])

  if (!userData || isLoading || seatGateLoading || freeSeatsPending || (isDeactivated && userDataFetching)) {
    return (
      <Modal
        show
        onHide={onHide}
        header={<></>}
        body={
          <div className='d-flex align-items-center justify-content-center p-4'>
            <Spinner />
          </div>
        }
        showFooterActions={false}
        centered={true}
      />
    )
  }

  if (purchaseTarget) {
    const isPurchaseBusy = isBuying || isUpdating
    return (
      <Modal
        show
        onHide={() => {
          if (!isPurchaseBusy) {
            onHide()
          }
        }}
        header={<h4>Buy a seat</h4>}
        headerBodySeparator
        body={
          <Container>
            <Row>
              <Col className='p-0'>
                <div className='paragraph-medium pb-2'>You&apos;re about to buy 1 {seatTypeLabels[purchaseTarget.seatType]} seat to complete the edit.</div>
                <SeatPurchaseSummary preview={preview} />
                <div className='paragraph-small zocks-text-gray-600 pt-3'>
                  You can also manage your seats on the{' '}
                  <Link to='/account/plan' className='zocks-text-primary'>
                    Plan page
                  </Link>
                  .
                </div>
                {buyError ? <FormFeedback>{buyError}</FormFeedback> : null}
              </Col>
            </Row>
          </Container>
        }
        showFooterActions
        onFooterPrimaryAction={handleBuyConfirm}
        footerPrimaryActionLoading={isPurchaseBusy}
        footerSecondaryActionDisabled={isPurchaseBusy}
        footerPrimaryActionTitle='Confirm'
        centered
      />
    )
  }

  const displayName = getUserDisplayName(userData.data)
  const sharedDataLabel = documentSharingAvailable ? 'past meetings, contacts and documents' : 'past meetings and contacts'

  const sharingBody = (
    <div className='d-flex flex-column gap-4'>
      <p className='paragraph-medium mb-0'>
        Give another team members access to {displayName}'s <strong>{sharedDataLabel}</strong>. They can view and edit this data, but they don't gain ownership.
      </p>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex justify-content-between'>
          <span className='paragraph-medium'>Share {sharedDataLabel} with team members</span>
          <Form.Switch disabled={accountUserSettingsLoading} checked={sharingEnabled} onChange={handleSharingEnabledChanged} />
        </div>
        <div>
          {sharingEnabled && (
            <UserShareForm
              shareType='meeting'
              attachedShareTypes={['contact']}
              noSelectedUserText='Share access with all team members'
              multiSelectWarningText='If no specific users are selected data will be shared with all team members.'
              hideSaveButton
              onSelectedUsersChange={setSelectedUserIds}
              userId={userId}
              showInModal
            />
          )}
        </div>
      </div>
    </div>
  )

  const editBody = (
    <Container>
      <Row>
        <FormProvider {...form}>
          <EditUserModalForm userId={userId} onHide={onHide} seatImpact={seatImpact} canBuySeats={canBuySeats} roleChangeError={roleChangeError} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show
      onHide={onHide}
      header={isDeactivated ? <h4>Manage sharing</h4> : <h4>Edit User</h4>}
      headerBodySeparator
      body={isDeactivated ? sharingBody : editBody}
      showFooterActions={true}
      onFooterPrimaryAction={isDeactivated ? handleShareSave : handleSubmit(handleEditSave)}
      footerPrimaryActionLoading={isDeactivated ? isSaving : isUpdating || isRolesUpdating}
      footerPrimaryActionDisabled={
        isDeactivated
          ? isSaving || accountUserSettingsLoading
          : (seatImpact.kind === 'blocked' && !canBuySeats) || seatImpact.kind === 'notOffered'
            ? true
            : undefined
      }
      footerPrimaryActionTitle={isDeactivated ? 'Save' : seatImpact.kind === 'blocked' && canBuySeats ? 'Buy a seat' : undefined}
      footerPrimaryActionButtonVariant={isDeactivated ? 'primary' : undefined}
      footerSecondaryActionDisabled={isDeactivated ? isSaving : undefined}
      centered
    />
  )
}

type RoleDisplay = Role & { disabled: boolean }

const sameRoleIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id))

const readApiMessage = (error: unknown, fallback: string) => {
  const body = typeof error === 'object' && error !== null && 'data' in error ? error.data : null
  if (isApiErrors(body) && body.errors.length) {
    return body.errors[0].message
  }
  return fallback
}

interface EditUserModalFormProps extends EditUserModalProps {
  seatImpact: RoleChangeSeatImpact
  canBuySeats: boolean
  roleChangeError: string | null
}

const EditUserModalForm: React.FC<EditUserModalFormProps> = ({ userId, seatImpact, canBuySeats, roleChangeError }) => {
  const { register, control, setValue } = useFormContext<FormValues>()

  const accountId = useAppSelector(selectAccountId)
  const roleGroups = useInviteRoleGroups()

  const { data: userData, isFetching: isFetchingUserData } = useGetUserByIdQuery({ accountId: accountId, userId: userId })

  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    {
      accountId: accountId,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const areUserRolesInGroup = !!getRoleGroupForRoles(userData?.data.roles.map((role) => role.id) ?? [])

  useEffect(() => {
    if (userData) {
      setValue('firstName', userData.data.firstName ?? '')
      setValue('lastName', userData.data.lastName ?? '')
      setValue('email', userData.data.email ?? '')
      setValue(
        'roleIds',
        userData.data.roles.map((role) => role.id),
      )
    }
  }, [userData, setValue])

  const roles = useMemo<RoleDisplay[]>(() => {
    if (!roleData || !userData) {
      return []
    }
    const roleIds = roleGroups.map((rg) => rg.roleIds).flat()
    const userRoles = userData.data.roles.map((ur) => ur.id)
    return roleData.data
      .filter((role) => {
        return roleIds.includes(role.id) || userRoles.includes(role.id)
      })
      .map((role) => {
        const roleGroup = getRoleGroupForRoles([role.id])
        return {
          ...role,
          name: roleGroup?.name || role.name,
          disabled: !roleIds.includes(role.id) && userRoles.includes(role.id),
        }
      })
  }, [roleData, userData, roleGroups])

  const handleRoleGroupSelected = (option: SelectOption) => {
    const roleGroup = roleGroups.find((e) => e.name === option.id)
    setValue('roleIds', roleGroup?.roleIds ?? [])
  }

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email address is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address*</Form.Label>
                    <Form.Control id={field.name} type='email' disabled={true} placeholder='Enter email address…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='firstName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>First name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='lastName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>Last name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          {/*<Row>*/}
          {/*  <Col>*/}
          {/*    <Controller*/}
          {/*      name='phoneNumber'*/}
          {/*      control={control}*/}
          {/*      rules={{*/}
          {/*        pattern: { value: /^\+[1-9][0-9]{0,24}$/i, message: 'Invalid phone number. E.g. +11234567890' },*/}
          {/*      }}*/}
          {/*      render={({ field, fieldState }) => (*/}
          {/*        <>*/}
          {/*          <Form.Label>Phone number</Form.Label>*/}
          {/*          <Form.Control id={field.name} type='text' placeholder='Enter phone number…' {...field} />*/}
          {/*        </>*/}
          {/*      )}*/}
          {/*    />*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row>
            <Col>
              <Controller
                name='roleIds'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>Roles</Form.Label>
                    {areUserRolesInGroup ? (
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={roleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={roleGroups
                          .map((roleGroup) => {
                            return { id: roleGroup.name, label: roleGroup.name }
                          })
                          .find((option) => option.id === getRoleGroupForRoles(field.value)?.name)}
                        onOptionSelected={handleRoleGroupSelected}
                      />
                    ) : (
                      <MultiSelectDropdown
                        register={register}
                        name='roleIds'
                        options={
                          roles.map((role) => {
                            return { id: role.id, label: getRoleDisplayName(role), disabled: role.disabled }
                          }) || []
                        }
                        initialSelectedOptions={field.value}
                      />
                    )}
                    {seatImpact.kind === 'blocked' ? (
                      <SeatInfoBox title={`No ${seatTypeLabels[seatImpact.seatType]} seats available`}>
                        {canBuySeats
                          ? `Buy an ${seatTypeLabels[seatImpact.seatType]} seat to save this change. It frees up 1 ${seatTypeLabels[seatImpact.releasedSeatType]} seat.`
                          : "You'll be able to make this change when a seat becomes available."}
                      </SeatInfoBox>
                    ) : null}
                    {seatImpact.kind === 'notOffered' ? (
                      <SeatInfoBox title={ASSISTANT_SEATS_NOT_OFFERED}>
                        Upgrade on the{' '}
                        <Link to='/account/plan' className='zocks-text-primary'>
                          Plan page
                        </Link>{' '}
                        {ASSISTANT_SEATS_UPGRADE}
                      </SeatInfoBox>
                    ) : null}
                    {seatImpact.kind === 'released' ? <SeatInfoBox>This change will free up 1 {seatTypeLabels[seatImpact.seatType]} seat.</SeatInfoBox> : null}
                    {roleChangeError ? <FormFeedback className='mt-2'>{roleChangeError}</FormFeedback> : null}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
